/**
 * Marketplaces on the item page: one row per storefront, whether the item is
 * live there, failed, waiting, ready to post or blocked, and the action that
 * fits. Posting, repricing and take-downs are carried out by a paired browser
 * extension (Settings → Browser extension); this panel queues them and shows
 * how they went. Listings entered by hand (marketplaces nothing can post to,
 * or readiness "listed_by_hand") keep the manual edit / end / relist / remove
 * actions.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { CrosslistRequest, CrosslistState, CrosslistTarget, ItemDetail, ListingRemoteAction, ListingUpsert } from "@sable/api-contract";
import { IconAlert, IconChevron, IconExternal, IconRetry, IconTrash } from "../../components/icons.tsx";
import { ErrorBlock, Loading, MarketplaceBadge, MoneyInput, StatusPill } from "../../components/ui.tsx";
import { t, tk } from "../../i18n/index.ts";
import { request } from "../../lib/api.ts";
import { money, shortDate } from "../../lib/format.ts";
import { keys, useCrosslist, useItemMutation } from "../../lib/queries.ts";
import { useCan, useWs } from "../../lib/workspace.tsx";
import { IN_STOCK, ListingDialog, activeSale, useMarketplaceName } from "./panels.tsx";

/** Readiness values the API refuses to queue. */
const UNQUEUEABLE = ["not_built", "off_in_country", "listed_by_hand", "blocked"];
/** Readiness values where the listing is managed by hand, not by the extension. */
const MANUAL = ["not_built", "listed_by_hand"];

type ItemListing = ItemDetail["listings"][number];
type RemoteListing = NonNullable<CrosslistTarget["listing"]>;
interface Row {
  marketplace: string;
  target: CrosslistTarget | null;
  /** The listing as the crosslister knows it (sync errors, external id). */
  remote: RemoteListing | null;
  /** The listing as the item knows it (listed date, hand-entered listings). */
  listing: ItemListing | null;
}

const statusOf = (r: Row) => r.remote?.status ?? r.listing?.status ?? null;
const isManual = (r: Row) => !r.target || MANUAL.includes(r.target.readiness);

function buildRows(item: ItemDetail, targets: CrosslistTarget[]): Row[] {
  const codes = [
    ...targets.map((x) => x.marketplace as string),
    ...item.listings.map((l) => l.marketplace).filter((c) => !targets.some((x) => x.marketplace === c)),
  ];
  const rows = codes.map((code) => {
    const target = targets.find((x) => x.marketplace === code) ?? null;
    return { marketplace: code, target, remote: target?.listing ?? null, listing: item.listings.find((l) => l.marketplace === code) ?? null };
  });
  // Live first, then anything needing attention, then waiting, then what could be posted, then the rest.
  const rank = (r: Row) => {
    const s = statusOf(r);
    if (s === "active") return 0;
    if (s === "error" || r.remote?.syncError) return 1;
    if (s === "queued") return 2;
    if (s) return 3;
    if (r.target && !UNQUEUEABLE.includes(r.target.readiness)) return 4;
    return 5;
  };
  return rows.map((r, i) => ({ r, i })).sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i).map((x) => x.r);
}

/** Queue posts and remote actions, and drop the new crosslist state into the cache. */
function useCrosslistActions(item: ItemDetail) {
  const ws = useWs();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function run(fn: () => Promise<CrosslistState>) {
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(keys.crosslist(ws.id, item.id), await fn());
      void qc.invalidateQueries({ queryKey: keys.item(ws.id, item.id) });
      void qc.invalidateQueries({ queryKey: keys.items(ws.id) });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }

  return {
    busy,
    error,
    post: (targets: CrosslistRequest["targets"]) => run(() => request<CrosslistState>(`/items/${item.id}/crosslist`, {
      method: "POST", workspace: ws.id, body: { targets } satisfies CrosslistRequest,
    })),
    remote: (listingId: string, action: ListingRemoteAction["action"]) => run(() =>
      request<CrosslistState>(`/items/${item.id}/listings/${listingId}/remote`, { method: "POST", workspace: ws.id, body: { action } satisfies ListingRemoteAction })),
  };
}

/** Listings entered by hand. They return the item; the crosslist view is refetched after. */
function useManualListing(item: ItemDetail) {
  const ws = useWs();
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: keys.crosslist(ws.id, item.id) });
  const upsert = useItemMutation(ws.id, item.id, (body: ListingUpsert) =>
    request<ItemDetail>(`/items/${item.id}/listings`, { method: "PUT", workspace: ws.id, body }));
  const remove = useItemMutation(ws.id, item.id, (listingId: string) =>
    request<ItemDetail>(`/items/${item.id}/listings/${listingId}`, { method: "DELETE", workspace: ws.id }));
  return {
    upsert: (body: ListingUpsert) => upsert.mutate(body, { onSuccess: refresh }),
    remove: (id: string) => remove.mutate(id, { onSuccess: refresh }),
    busy: upsert.isPending || remove.isPending,
    error: upsert.error ?? remove.error,
  };
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function MarketRow({ item, row, name, queueable, selected, price, onToggle, onPrice, xl, manual, onEdit, onPreview, queueOk, manualOk }: {
  item: ItemDetail;
  row: Row;
  name: string;
  queueable: boolean;
  selected: boolean;
  price: number | null;
  onToggle: (on: boolean) => void;
  onPrice: (minor: number | null) => void;
  xl: ReturnType<typeof useCrosslistActions>;
  manual: ReturnType<typeof useManualListing>;
  onEdit: (l: ItemListing) => void;
  onPreview: () => void;
  queueOk: boolean;
  manualOk: boolean;
}) {
  const ws = useWs();
  const canWrite = useCan("listings.write");
  const { target, remote: rl, listing: il } = row;
  const status = statusOf(row);
  const listPrice = rl?.listPriceMinor ?? il?.listPriceMinor ?? null;
  const currency = rl?.currency ?? il?.currency ?? item.currency;
  const url = rl?.externalUrl ?? il?.externalUrl ?? null;
  const handMade = isManual(row);
  const failed = status === "error" || Boolean(rl?.syncError);

  const canRepost = Boolean(target?.actions.includes("publish") && rl && ["error", "delisted", "ended"].includes(rl.status) && queueOk);
  const canDelist = Boolean(target?.actions.includes("delist") && rl?.externalId && ["active", "error"].includes(rl.status));
  const canCancel = Boolean(rl && !rl.externalId && rl.status === "queued");

  let sub: string;
  if (rl?.syncError) sub = rl.syncError;
  else if (status === "active") sub = [listPrice !== null ? money(listPrice, currency) : null, il?.listedAt ? t("crosslist.liveSince", { date: shortDate(il.listedAt) }) : null].filter(Boolean).join(" · ");
  else if (target && !status) sub = [tk("crosslist.readiness", target.readiness), ...target.preview.blockers].join(" · ");
  else sub = target ? tk("crosslist.readiness", target.readiness) : "";
  // Whose shop it is in, and which paired browser put it there.
  const where = [
    rl?.accountHandle ? t("crosslist.account", { handle: rl.accountHandle }) : rl?.accountId ? t("crosslist.accountId", { id: rl.accountId }) : null,
    rl?.postedFrom ? t("crosslist.postedFrom", { device: rl.postedFrom }) : null,
  ].filter(Boolean).join(" · ");

  return (
    <li className={`ip-row${selected ? " is-selected" : ""}${failed ? " is-error" : ""}`}>
      <div className="ip-row-main">
        <span className="ip-check">
          {queueable && <input type="checkbox" checked={selected} aria-label={name} onChange={(e) => onToggle(e.target.checked)} />}
        </span>
        <MarketplaceBadge code={row.marketplace} />
        <div className="ip-row-text">
          <div className="ip-row-title">
            <span className="ip-strong">{name}</span>
            {status && <StatusPill status={status} kind="listingStatus" />}
          </div>
          {sub && <div className={`small ${failed ? "ip-err-text" : "muted"}`}>{sub}</div>}
          {where && <div className="small muted">{where}</div>}
        </div>

        <div className="ip-row-actions">
          {queueable && (
            <div className="ip-price">
              <MoneyInput currency={item.currency} locale={ws.locale} value={price} onChange={onPrice} />
            </div>
          )}
          {!queueable && status !== "active" && listPrice !== null && <span className="num ip-price-text">{money(listPrice, currency)}</span>}
          {target && <button className="btn btn-sm btn-ghost" onClick={onPreview}>{t("crosslist.preview")}</button>}

          {canWrite && !handMade && canRepost && (
            <button className="btn btn-sm" disabled={xl.busy} onClick={() => void xl.remote(rl!.id, "publish")}><IconRetry size={14} />{t("crosslist.retry")}</button>
          )}
          {canWrite && !handMade && canDelist && (
            <button className="btn btn-sm btn-ghost" disabled={xl.busy}
              onClick={() => { if (confirm(t("crosslist.takeDownConfirm", { marketplace: name }))) void xl.remote(rl!.id, "delist"); }}>
              {t("crosslist.takeDown")}
            </button>
          )}
          {canWrite && !handMade && canCancel && (
            <button className="btn btn-sm btn-ghost" disabled={xl.busy} onClick={() => void xl.remote(rl!.id, "delist")}>{t("crosslist.cancel")}</button>
          )}

          {canWrite && manualOk && handMade && il && (il.status === "active" ? (
            <>
              <button className="btn btn-sm btn-ghost" onClick={() => onEdit(il)}>{t("app.edit")}</button>
              <button className="btn btn-sm btn-ghost" disabled={manual.busy}
                onClick={() => manual.upsert({ marketplace: il.marketplace, status: "ended" })}>{t("item.endListing")}</button>
            </>
          ) : (
            <>
              <button className="btn btn-sm btn-ghost" disabled={manual.busy}
                onClick={() => manual.upsert({ marketplace: il.marketplace, status: "active" })}>
                {il.status === "queued" ? t("item.markLive") : t("item.relist")}
              </button>
              <button className="icon-btn" aria-label={t("item.removeListing")} disabled={manual.busy}
                onClick={() => manual.remove(il.id)}><IconTrash size={16} /></button>
            </>
          ))}

          {url && (
            <a className="icon-btn" href={url} target="_blank" rel="noreferrer noopener" aria-label={t("crosslist.open", { marketplace: name })}>
              <IconExternal size={18} />
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

/** `onPreview` opens that marketplace's tab in the posting preview. */
export function MarketplacesPanel({ item, onPreview }: { item: ItemDetail; onPreview: (marketplace: string) => void }) {
  const ws = useWs();
  const canWrite = useCan("listings.write");
  const q = useCrosslist(ws.id, item.id);
  const name = useMarketplaceName();
  const xl = useCrosslistActions(item);
  const manual = useManualListing(item);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  const [dialog, setDialog] = useState<{ open: boolean; listing?: ItemListing }>({ open: false });

  const manualOk = IN_STOCK.includes(item.status);
  const queueOk = manualOk && !item.archivedAt;
  const rows = buildRows(item, q.data?.targets ?? []);
  // The API takes a post for a marketplace that already has a listing: a live one is repriced, any other posted again.
  const queueable = (r: Row) => canWrite && queueOk && r.target !== null && !UNQUEUEABLE.includes(r.target.readiness);
  const priceOf = (r: Row) => (r.marketplace in prices ? prices[r.marketplace]! : r.target?.priceMinor ?? null);
  const chosen = rows.filter((r) => picked[r.marketplace] && queueable(r));
  const complete = chosen.length > 0 && chosen.every((r) => (priceOf(r) ?? 0) > 0);
  const jobs = q.data?.jobs ?? [];
  const latest = jobs[0];

  const post = async () => {
    if (await xl.post(chosen.map((r) => ({ marketplace: r.marketplace as CrosslistRequest["targets"][number]["marketplace"], priceMinor: priceOf(r)! })))) setPicked({});
  };

  return (
    <section className="card" aria-labelledby="ip-markets">
      <div className="ip-card-head">
        <div>
          <h2 id="ip-markets">{t("item.marketplaces")}</h2>
          {q.data && <p className="muted small">{t("crosslist.devices", { count: q.data.devices.length })}</p>}
        </div>
        {canWrite && manualOk && <button className="btn btn-sm btn-ghost" onClick={() => setDialog({ open: true })}>{t("item.addListing")}</button>}
      </div>

      {q.isPending && <Loading />}
      {q.isError && <ErrorBlock error={q.error} onRetry={() => void q.refetch()} />}
      {!q.isPending && rows.length === 0 && <p className="muted small">{t("item.noListings")}</p>}

      {rows.length > 0 && (
        <ul className="ip-rows">
          {rows.map((r) => (
            <MarketRow
              key={r.marketplace}
              item={item}
              row={r}
              name={name(r.marketplace)}
              queueable={queueable(r)}
              selected={Boolean(picked[r.marketplace])}
              price={priceOf(r)}
              onToggle={(on) => setPicked({ ...picked, [r.marketplace]: on })}
              onPrice={(minor) => setPrices({ ...prices, [r.marketplace]: minor })}
              xl={xl}
              manual={manual}
              onEdit={(l) => setDialog({ open: true, listing: l })}
              onPreview={() => onPreview(r.marketplace)}
              queueOk={queueOk}
              manualOk={manualOk}
            />
          ))}
        </ul>
      )}

      {rows.some(queueable) && (
        <div className="ip-card-foot">
          <span className="muted small">{chosen.length > 0 ? t("crosslist.viaExtension") : t("crosslist.pickHint")}</span>
          <button className="btn btn-primary btn-sm" disabled={!complete || xl.busy} onClick={() => void post()}>
            {xl.busy ? t("crosslist.posting") : t("crosslist.post", { count: chosen.length })}
          </button>
        </div>
      )}
      {xl.error != null && <p className="form-error">{errorText(xl.error)}</p>}
      {manual.error && <p className="form-error">{manual.error.message}</p>}

      {jobs.length > 0 && (
        <details className="ip-activity">
          <summary>
            <span className="ip-label">{t("crosslist.recent")}</span>
            {latest && <span className="muted small grow">{tk("job.action", latest.action)} · {name(latest.marketplace)} · {shortDate(latest.createdAt)}</span>}
            <IconChevron size={16} />
          </summary>
          <div className="list">
            {jobs.slice(0, 8).map((j) => (
              <div className="list-row" key={j.id}>
                <MarketplaceBadge code={j.marketplace} />
                <div className="grow">
                  <div className="name">{tk("job.action", j.action)} · {name(j.marketplace)}</div>
                  <div className="sub">
                    {shortDate(j.createdAt)}
                    {j.attempts > 1 ? ` · ${t("job.attempt", { count: j.attempts })}` : ""}
                    {j.lastError ? ` · ${j.lastError}` : ""}
                  </div>
                </div>
                <span className={`pill pill-job-${j.status}`}>{tk("job.status", j.status)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {dialog.open && <ListingDialog item={item} initial={dialog.listing} onClose={() => setDialog({ open: false })} />}
    </section>
  );
}

// ---- After a sale: what is still coming down -----------------------------------------------

/**
 * The sale trigger marks every other listing delisted at once and queues a
 * delist job where a worker can reach it (0004_crosslist). What is left for a
 * person: jobs still waiting for the browser, jobs that failed, and listings
 * nothing can reach, which are still up on the marketplace itself.
 */
export function TakedownBanner({ item }: { item: ItemDetail }) {
  const ws = useWs();
  const q = useCrosslist(ws.id, item.id);
  const name = useMarketplaceName();
  const sale = activeSale(item);
  if (!sale || !q.data) return null;

  const { targets, jobs } = q.data;
  // Recorded with today's date, a sale sits at noon and can postdate the trigger's ended_at.
  const since = new Date(sale.soldAt).getTime() - 86_400_000;
  const lines: { marketplace: string; text: string; tone: "wait" | "act"; url: string | null }[] = [];

  for (const l of item.listings) {
    if (l.marketplace === sale.marketplace || l.status !== "delisted" || !l.endedAt || new Date(l.endedAt).getTime() < since) continue;
    const mp = name(l.marketplace);
    const target = targets.find((x) => x.marketplace === l.marketplace);
    const job = jobs.find((j) => j.marketplace === l.marketplace && j.action === "delist");
    if (job && (job.status === "queued" || job.status === "running") && target?.actions.includes("delist")) {
      lines.push({ marketplace: l.marketplace, text: t("crosslist.takedown.waiting", { marketplace: mp }), tone: "wait", url: l.externalUrl });
    } else if (job?.status === "failed") {
      lines.push({ marketplace: l.marketplace, text: t("crosslist.takedown.failed", { marketplace: mp, error: job.lastError ?? "—" }), tone: "act", url: l.externalUrl });
    } else if (job?.status !== "succeeded" && !sale.shippedAt) {
      lines.push({ marketplace: l.marketplace, text: t("crosslist.takedown.byHand", { marketplace: mp }), tone: "act", url: l.externalUrl });
    }
  }
  if (lines.length === 0) return null;

  return (
    <div className="banner ip-takedown" role="status">
      <IconAlert size={20} />
      <div className="grow stack-sm">
        <strong>{t("crosslist.takedown.title")}</strong>
        <span className="small">{t("crosslist.takedown.hint")}</span>
        <ul className="ip-takedown-list">
          {lines.map((x) => (
            <li key={x.marketplace} className={x.tone === "act" ? "is-act" : ""}>
              <span className="grow">{x.text}</span>
              {x.url && (
                <a className="icon-btn" href={x.url} target="_blank" rel="noreferrer noopener" aria-label={t("crosslist.open", { marketplace: name(x.marketplace) })}>
                  <IconExternal size={16} />
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
